import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import {
  History,
  Search,
  Filter,
  Download,
  Eye,
  FileText,
  Truck,
  Network,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Loader2 } from
'lucide-react';

import { oigAPI } from '@/shared/services/api';

export const ImportHistory = () => {const { t } = useTranslation("oig");
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [importHistory, setImportHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const data = await oigAPI.getImportHistory();
        setImportHistory(data || []);
      } catch (err) {
        console.error('Failed to fetch import history:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const filteredHistory = importHistory.filter((item) => {
    if (filterType !== 'all' && item.tipo !== filterType) return false;
    if (filterStatus !== 'all' && (item.status || '').toLowerCase() !== filterStatus) return false;
    if (searchQuery && !(item.arquivo || '').toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const statusColors = {
    'Sucesso': 'bg-emerald-100 text-emerald-700',
    'Erro': 'bg-red-100 text-red-700',
    'Parcial': 'bg-amber-100 text-amber-700'
  };

  const typeIcons = {
    'NF-e': FileText,
    'CT-e': Truck,
    'EDI': Network,
    'CSV': FileSpreadsheet
  };

  const typeColors = {
    'NF-e': 'bg-emerald-100 text-emerald-600',
    'CT-e': 'bg-blue-100 text-blue-600',
    'EDI': 'bg-violet-100 text-violet-600',
    'CSV': 'bg-orange-100 text-orange-600'
  };

  return (
    <div className="space-y-6" data-testid="oig-history">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <History className="h-8 w-8 text-cyan-600" />{t("histricoDeImportaes", "Hist\xF3rico de Importa\xE7\xF5es")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("registroCompletoDe", "Registro completo de todas as importa\xE7\xF5es realizadas")}

          </p>
        </div>
        <Button variant="outline" className="gap-2">
          <Download className="h-4 w-4" />{t("exportarRelatrio", "Exportar Relat\xF3rio")}

        </Button>
      </div>

      {/* Filters */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por arquivo..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} />
              
            </div>
            <div className="flex gap-2">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("todos", "Todos")}</SelectItem>
                  <SelectItem value="NF-e">{t("nfe", "NF-e")}</SelectItem>
                  <SelectItem value="CT-e">{t("cte", "CT-e")}</SelectItem>
                  <SelectItem value="EDI">{t("edi", "EDI")}</SelectItem>
                  <SelectItem value="CSV">{t("csv", "CSV")}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("todos", "Todos")}</SelectItem>
                  <SelectItem value="sucesso">{t("sucesso", "Sucesso")}</SelectItem>
                  <SelectItem value="erro">{t("erro", "Erro")}</SelectItem>
                  <SelectItem value="parcial">{t("parcial", "Parcial")}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" className="gap-1">
                <Calendar className="h-4 w-4" />{t("data", "Data")}

              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History Table */}
      <Card className="card-elevated">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cyan-700 text-white">
                <tr>
                  <th className="py-3 px-4 text-left font-medium">{t("tipo", "Tipo")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("arquivo", "Arquivo")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("datahora", "Data/Hora")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("status", "Status")}</th>
                  <th className="py-3 px-4 text-center font-medium">{t("registros", "Registros")}</th>
                  <th className="py-3 px-4 text-center font-medium">{t("erros", "Erros")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("usurio", "Usu\xE1rio")}</th>
                  <th className="py-3 px-4 text-left font-medium">{t("asnGerado", "ASN Gerado")}</th>
                  <th className="py-3 px-4 text-center font-medium">{t("aes", "A\xE7\xF5es")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((item, index) => {
                  const Icon = typeIcons[item.tipo];
                  return (
                    <tr
                      key={item.id}
                      className={`border-b border-border hover:bg-muted/30 ${index % 2 === 0 ? 'bg-white dark:bg-transparent' : 'bg-gray-50 dark:bg-muted/10'}`
                      }>
                      
                      <td className="py-3 px-4">
                        <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-lg ${typeColors[item.tipo]}`}>
                          <Icon className="h-4 w-4" />
                          <span className="font-medium text-sm">{item.tipo}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-mono text-sm">{item.arquivo}</p>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {new Date(item.dataImportacao).toLocaleString('pt-BR')}
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={statusColors[item.status]}>
                          {item.status === 'Sucesso' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                          {item.status === 'Erro' && <XCircle className="h-3 w-3 mr-1" />}
                          {item.status === 'Parcial' && <AlertTriangle className="h-3 w-3 mr-1" />}
                          {item.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="font-medium text-emerald-600">{item.registros}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {item.erros ?
                        <span className="font-medium text-red-600">{item.erros}</span> :

                        <span className="text-muted-foreground">-</span>
                        }
                      </td>
                      <td className="py-3 px-4 text-sm">{item.usuario}</td>
                      <td className="py-3 px-4">
                        {item.asnGerado ?
                        <Badge variant="outline" className="font-mono text-xs">
                            {item.asnGerado}
                          </Badge> :

                        <span className="text-muted-foreground text-sm">-</span>
                        }
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button variant="ghost" size="icon">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">{t("mostrando", "Mostrando")}
              {filteredHistory.length}{t("de", "de")}{importHistory.length}{t("registros", "registros")}
            </p>
            <div className="flex items-center gap-2">
              <Select defaultValue="25">
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">{t("25", "25")}</SelectItem>
                  <SelectItem value="50">{t("50", "50")}</SelectItem>
                  <SelectItem value="100">{t("100", "100")}</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" disabled>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="default" size="sm" className="bg-cyan-600">1</Button>
                <Button variant="outline" size="icon" disabled>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Details Card */}
      {filteredHistory.some((h) => h.mensagemErro) &&
      <Card className="card-elevated border-red-200 bg-red-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />{t("errosRecentes", "Erros Recentes")}

          </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {filteredHistory.filter((h) => h.mensagemErro).map((item) =>
            <div key={item.id} className="p-3 rounded-lg bg-white border border-red-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm">{item.arquivo}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(item.dataImportacao).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <p className="text-sm text-red-700">{item.mensagemErro}</p>
                </div>
            )}
            </div>
          </CardContent>
        </Card>
      }
    </div>);

};